import { memo, useState } from 'react'
import type { ColDef, ColGroupDef, ITooltipParams } from 'ag-grid-community'
import { cx } from 'class-variance-authority'
import { CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'

import { imgPath } from '@/services/adapters'

type ImageTooltipValue = {
  name?: unknown
  pictures?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isImageTooltipValue = (value: unknown): value is ImageTooltipValue =>
  isRecord(value) && ('pictures' in value || 'name' in value)

const isPicturesColumn = (
  colDef: ColDef | ColGroupDef | null | undefined,
): boolean => {
  return Boolean(colDef && 'field' in colDef && colDef.field === 'pictures')
}

const normalizePictures = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (picture): picture is string =>
        typeof picture === 'string' && picture.length > 0,
    )
  }

  return typeof value === 'string' && value.length > 0 ? [value] : []
}

const resolveImageAlt = (name: unknown): string => {
  if (typeof name === 'string' && name.length > 0) return name

  if (isRecord(name)) {
    const label = name.label
    if (typeof label === 'string' && label.length > 0) return label
  }

  return 'Product preview'
}

const isSvgPath = (path: string): boolean => {
  const sanitizedPath = path.split('?')[0]?.split('#')[0] ?? path
  return sanitizedPath.toLowerCase().endsWith('.svg')
}

export const ImageTooltip = memo(
  (props: ITooltipParams & { colDef: ColDef }) => {
    const { data, hideTooltipCallback, colDef, value } = props

    const tooltipValue = isImageTooltipValue(value) ? value : undefined
    const rowData = isRecord(data) ? data : undefined
    const name = tooltipValue?.name ?? rowData?.name
    const pictures = normalizePictures(
      tooltipValue?.pictures ?? rowData?.pictures ?? value,
    )
    const picture = pictures[0]
    const [preferredPicture, setPreferredPicture] = useState<
      string | undefined
    >()
    const selectedPicture =
      preferredPicture && pictures.includes(preferredPicture)
        ? preferredPicture
        : picture

    const closeLightbox = () => {
      hideTooltipCallback && hideTooltipCallback()
    }

    if (!picture || !isPicturesColumn(colDef)) return null
    const imagePath = imgPath(selectedPicture ?? picture)

    const onError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      e.currentTarget.src = `${process.env.NEXT_PUBLIC_ASSETS_PATH}/thumbnail/default.png`
      e.currentTarget.onerror = null
      return true
    }

    const isSvg = isSvgPath(imagePath)

    return (
      <div className="relative flex size-60 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
        <img
          src={imagePath}
          alt={resolveImageAlt(name)}
          className={cx({
            'size-full object-contain': !isSvg,
            'max-h-32 max-w-32 object-contain': isSvg,
          })}
          onError={onError}
        />
        <button
          type="button"
          aria-label="Close preview"
          className="absolute right-2 top-2 rounded-full bg-slate-300/25 p-2 hover:bg-slate-300/50"
          onClick={closeLightbox}
        >
          <XIcon className="size-4 text-white" />
        </button>
        {pictures?.length > 1 && (
          <div className="absolute bottom-4 flex w-full items-center justify-center space-x-4">
            <NavigationButton
              direction="left"
              pictures={pictures}
              setSelectedPicture={setPreferredPicture}
              selectedPicture={selectedPicture}
            />
            <NavigationButton
              direction="right"
              pictures={pictures}
              setSelectedPicture={setPreferredPicture}
              selectedPicture={selectedPicture}
            />
          </div>
        )}
      </div>
    )
  },
)
ImageTooltip.displayName = 'ImageTooltip'

const NavigationButton = ({
  direction,
  setSelectedPicture,
  pictures,
  selectedPicture,
}: {
  direction: 'left' | 'right'
  setSelectedPicture: (picture: string | undefined) => void
  pictures: string[]
  selectedPicture: string | undefined
}) => {
  const changePicture = () => {
    const index =
      selectedPicture === undefined ? -1 : pictures.indexOf(selectedPicture)

    let nextIndex = index + (direction === 'left' ? -1 : 1)
    if (nextIndex < 0) nextIndex = pictures.length - 1
    if (nextIndex >= pictures.length) nextIndex = 0

    const nextImage = pictures?.[nextIndex]
    setSelectedPicture(nextImage)
  }

  return (
    <button
      type="button"
      aria-label={direction === 'left' ? 'Previous image' : 'Next image'}
      onClick={changePicture}
      className="rounded-full bg-slate-300/50 p-2 hover:bg-slate-300/90"
    >
      <CaretRightIcon
        className={cx({
          'size-8 text-white': true,
          'rotate-180 transform': direction === 'left',
        })}
      />
    </button>
  )
}
